import { requestMicPermission } from "./micPermission";
import { eventBus } from "../observability/eventBus";
import { makeEvent } from "../observability/eventSchema";
import { useChatStore } from "../state/chatStore";
import { AppError } from "../errors/types";

// mic lifecycle (spec §3.1.3): session 中 mic を 1 本の MediaStream として保持し、
// 複数の consumer (VAD / 音量メーター) が同じ track を参照できるようにする。
// 毎 turn の getUserMedia 再要求を廃止 (permission dialog がフラッシュする体験劣化を防ぐ)。
export class MediaStreamManager {
  private stream: MediaStream | null = null;
  private pending: Promise<MediaStream> | null = null;
  private preferredDeviceId: string | null = null;

  private adoptStream(s: MediaStream): void {
    this.stream = s;
    // spec §6.2 event #3 mic.acquire_succeeded / #5 mic.stream_ended
    // sessionId 未確定 (startSession 前) の hot path では emit を skip する
    // (CLAUDE.md 原則 5: R2 fire-and-forget、UI ブロック禁止)。
    const sessionId = useChatStore.getState().sessionId;
    if (sessionId) {
      const track = s.getAudioTracks()[0];
      // getSettings() は Node / test-mock で未実装のことがあるため optional chain。
      const settings = (track?.getSettings?.() ?? {}) as Record<string, unknown>;
      eventBus.emit(
        makeEvent("mic.acquire_succeeded", sessionId, {
          deviceId: settings.deviceId,
          sampleRate: settings.sampleRate,
          channelCount: settings.channelCount,
        })
      );
      track?.addEventListener?.("ended", () => {
        const sid = useChatStore.getState().sessionId;
        if (!sid) return;
        eventBus.emit(
          makeEvent("mic.stream_ended", sid, {
            reason: "track_ended",
            deviceId: settings.deviceId,
          })
        );
      });
    }
  }

  async ensureStream(): Promise<MediaStream> {
    if (this.stream && this.stream.getTracks().some((t) => t.readyState === "live")) {
      return this.stream;
    }
    if (this.pending) return this.pending;
    this.pending = requestMicPermission(this.preferredDeviceId ?? undefined)
      .catch((e: unknown) => {
        // 選択デバイスが抜かれた等。会話継続を優先し、選択を破棄して既定デバイスで
        // 1 回だけ retry (permission-denied は retry しても無意味なので対象外)
        if (this.preferredDeviceId && e instanceof AppError && e.code === "mic-no-audio") {
          this.preferredDeviceId = null;
          return requestMicPermission(undefined);
        }
        throw e;
      })
      .then((s) => {
        this.adoptStream(s);
        return s;
      })
      .catch((e: unknown) => {
        // spec §6.2 event #4 mic.acquire_failed (drop 対象外の critical event)
        const sessionId = useChatStore.getState().sessionId;
        if (sessionId) {
          const err = e as Error;
          eventBus.emit(
            makeEvent("mic.acquire_failed", sessionId, {
              name: err?.name ?? "Unknown",
              // spec §5.2 hard cap 2 KB / §7.3 diff: 500 char truncate
              message: (err?.message ?? "").slice(0, 500),
            })
          );
        }
        throw e;
      })
      .finally(() => {
        this.pending = null;
      });
    return this.pending;
  }

  pause(): void {
    if (!this.stream) return;
    this.stream.getTracks().forEach((t) => {
      t.enabled = false;
    });
  }

  resume(): void {
    if (!this.stream) return;
    this.stream.getTracks().forEach((t) => {
      t.enabled = true;
    });
  }

  stop(): void {
    if (!this.stream) return;
    // spec §6.3 event #5 mic.stream_ended {reason: "explicit"}
    // emit 前に track.getSettings() を呼んで deviceId を取得 (stop 後は read 不可)
    const sessionId = useChatStore.getState().sessionId;
    if (sessionId) {
      const track = this.stream.getAudioTracks()[0];
      const settings = (track?.getSettings?.() ?? {}) as Record<string, unknown>;
      eventBus.emit(
        makeEvent("mic.stream_ended", sessionId, {
          reason: "explicit",
          deviceId: settings.deviceId,
        })
      );
    }
    this.stream.getTracks().forEach((t) => t.stop());
    this.stream = null;
  }

  /**
   * 入力デバイスを取り直して差し替える (Preflight Step 2 用)。deviceId=null は
   * 「OS 既定へ戻す」(preference クリア)。新 stream の取得に成功してから旧 track を
   * stop するため、失敗時は旧 stream が生きたまま残る (呼び出し側は現状維持で案内を
   * 出せる)。VAD 稼働前 (Preflight 内) に確定させる前提なので、consumer の re-attach
   * は返り値の新 stream で行う契約。
   */
  async switchDevice(deviceId: string | null): Promise<MediaStream> {
    if (this.pending) await this.pending.catch(() => undefined);
    const next = await requestMicPermission(deviceId ?? undefined);
    const prev = this.stream;
    if (prev) {
      const sessionId = useChatStore.getState().sessionId;
      if (sessionId) {
        const track = prev.getAudioTracks()[0];
        const settings = (track?.getSettings?.() ?? {}) as Record<string, unknown>;
        eventBus.emit(
          makeEvent("mic.stream_ended", sessionId, {
            reason: "device_switch",
            deviceId: settings.deviceId,
          })
        );
      }
      prev.getTracks().forEach((t) => t.stop());
    }
    this.preferredDeviceId = deviceId;
    this.adoptStream(next);
    return next;
  }

  isActive(): boolean {
    if (!this.stream) return false;
    return this.stream.getTracks().some((t) => t.readyState === "live");
  }

  getStream(): MediaStream | null {
    return this.stream;
  }
}

// アプリ全体で singleton として使う。テストからは new MediaStreamManager() を使うこと。
export const mediaStreamManager = new MediaStreamManager();
